'use client'

import { useCallback, useRef, useState, type ReactNode } from 'react'

import { HeroCycler } from '@/components/hero-cycler'
import { TopoFlow, type TopoFlowHandle } from '@/components/topo-flow'

const HERO_PHRASES = [
  "Your Driveway's Washing Away?",
  "Your Retaining Wall's Giving Up?",
  "Your Block's a Muddy Mess?",
  "Your Driveway's Got Potholes?",
  'Your Earthworks Half-Done?',
  "Your Gravel's Down the Hill Again?",
]

const FIX_LINES = ['We Can Fix That!', "We'll Fix That!", 'We Fix That!', 'We Will Fix It!']

/** Each pose gives the fix line a different resting spot, tilt, and colours.
 *  Four distinct keepers, ordered to maximise contrast between consecutive cycles. */
const FIX_THAT_POSES = [
  // 1. White on purple, clean bright pop
  { colour: '#ffffff', highlight: 'var(--brand-purple)', x: -3, y: 1, r: -1.8 },
  // 2. Chartreuse on purple-dark, neon green on deep plum
  {
    colour: 'var(--brand-chartreuse-vivid)',
    highlight: 'var(--brand-purple-dark)',
    x: -2,
    y: 2,
    r: -0.9,
  },
  // 3. Lavender on forest green
  { colour: 'var(--brand-lavender)', highlight: 'var(--brand-green)', x: 5, y: -2, r: 2.0 },
  // 4. Lavender on chartreuse, bright counterpoint
  {
    colour: 'var(--brand-lavender)',
    highlight: 'var(--brand-chartreuse-vivid)',
    x: 6,
    y: 2,
    r: 1.5,
  },
]

interface HeroInteractiveProps {
  /** Static content rendered below the headline (CTA buttons, insurance copy) */
  children: ReactNode
}

export function HeroInteractive({ children }: HeroInteractiveProps) {
  const topoRef = useRef<TopoFlowHandle>(null)
  const [poseIndex, setPoseIndex] = useState(0)
  const [pop, setPop] = useState(false)
  const [fixFading, setFixFading] = useState(false)
  const pendingIndex = useRef(0)

  const handleCycle = useCallback((index: number) => {
    // Fade out the fix line before swapping text
    pendingIndex.current = index % FIX_THAT_POSES.length
    setFixFading(true)

    // After fade-out, swap text and fade back in
    setTimeout(() => {
      setPoseIndex(pendingIndex.current)
      setFixFading(false)
      setPop(true)
      setTimeout(() => setPop(false), 350)
    }, 300)
  }, [])

  const pose = FIX_THAT_POSES[poseIndex]

  return (
    <>
      <TopoFlow ref={topoRef} seed={1} lineCount={8} scale={0.002} speed={0.1} />
      <div className="relative z-10 mx-auto max-w-6xl px-4 sm:px-6">
        <div>
          <h1 className="leading-tight tracking-tight text-white">
            <span className="font-marker flex min-h-[2.5em] items-end text-3xl sm:block sm:min-h-0 sm:text-5xl sm:whitespace-nowrap lg:text-6xl">
              <HeroCycler phrases={HERO_PHRASES} topoFlowRef={topoRef} onCycle={handleCycle} />
            </span>
            <span
              className={[
                'hero-fix-that font-display mt-3 inline-block text-6xl whitespace-nowrap transition-opacity duration-300 sm:mt-4 sm:text-7xl lg:text-8xl',
                pop && 'hero-fix-that-pop',
                fixFading && 'opacity-0',
              ]
                .filter(Boolean)
                .join(' ')}
              style={
                {
                  color: pose.colour,
                  textShadow: `0 0 20px ${pose.colour}40`,
                  '--highlight-colour': pose.highlight,
                  transform: pop
                    ? `translate(${pose.x}px, ${pose.y}px) rotate(${pose.r}deg) scale(1.08)`
                    : `translate(${pose.x}px, ${pose.y}px) rotate(${pose.r}deg) scale(1)`,
                } as React.CSSProperties
              }
              data-text={FIX_LINES[poseIndex % FIX_LINES.length]}
            >
              {FIX_LINES[poseIndex % FIX_LINES.length]}
            </span>
          </h1>
          {children}
        </div>
      </div>
    </>
  )
}
